// Global packages, functions and components
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import { ProductImage } from '~/components'

// Types
import type { RefObject } from 'react'
import type { Splide as SplideInstance } from 'node_modules/@splidejs/react-splide/dist/types'

interface Props {
  thumbsRef: RefObject<SplideInstance>
  mainRef: RefObject<SplideInstance>
  images?: { imageUrl: string }[]
  promotionName?: string
  productName?: string
}

// Main export
const ImageCarousel = ({
  thumbsRef,
  mainRef,
  images,
  promotionName,
  productName,
}: Props) => (
  <>
    <div className="mb-6 flex aspect-square gap-7">
      <Splide
        ref={thumbsRef}
        hasTrack={false}
        className="hidden h-full py-14 md:block"
        options={{
          type: 'slide',
          perMove: 1,
          isNavigation: true,
          pagination: false,
          fixedWidth: 100,
          fixedHeight: 100,
          height: '100%',
          direction: 'ttb',
          gap: 10,
        }}
        aria-label="The carousel with thumbnails. Selecting a thumbnail will change the main carousel"
      >
        <SplideTrack className="h-full">
          {images &&
            images?.length > 0 &&
            images.map((e, i) => (
              <SplideSlide
                className="cursor-pointer rounded-lg border border-transparent [&.is-active]:border-teal-green"
                key={i}
              >
                <ProductImage
                  src={e.imageUrl}
                  alt={productName}
                  loading="lazy"
                  adaptive
                />
              </SplideSlide>
            ))}
        </SplideTrack>
        <div className="splide__arrows">
          <button
            type="button"
            className="splide__arrow splide__arrow--prev absolute left-1/2 top-0 flex size-10 -translate-x-1/2 items-center justify-center rounded-full border border-teal-green bg-transparent text-teal-green disabled:border-neutral-300 disabled:text-neutral-300"
          >
            <ChevronUpIcon className="size-6" />
          </button>
          <button
            type="button"
            className="splide__arrow splide__arrow--next absolute bottom-0 left-1/2 flex size-10 -translate-x-1/2 items-center justify-center rounded-full border border-teal-green bg-transparent text-teal-green disabled:border-neutral-300 disabled:text-neutral-300"
          >
            <ChevronDownIcon className="size-6" />
          </button>
        </div>
      </Splide>

      <Splide
        ref={mainRef}
        hasTrack={false}
        className="w-full rounded-2xl bg-white"
        options={{
          type: 'fade',
          perPage: 1,
          perMove: 1,
          pagination: false,
        }}
      >
        <SplideTrack className="h-full">
          {images &&
            images?.length > 0 &&
            images.map((e, i) => (
              <SplideSlide key={i}>
                <ProductImage
                  src={e.imageUrl}
                  alt={productName}
                  tags={
                    promotionName
                      ? [{ label: promotionName, style: 'red' }]
                      : []
                  }
                  loading={i === 0 ? 'eager' : 'lazy'}
                  priority={i === 0}
                  adaptive
                />
              </SplideSlide>
            ))}
        </SplideTrack>
        <div className="splide__arrows block md:hidden">
          <button
            type="button"
            className="splide__arrow splide__arrow--prev absolute -left-4 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-teal-green bg-transparent text-teal-green disabled:border-neutral-300 disabled:text-neutral-300"
          >
            <ChevronLeftIcon className="size-6" />
          </button>
          <button
            type="button"
            className="splide__arrow splide__arrow--next absolute -right-4 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-teal-green bg-transparent text-teal-green disabled:border-neutral-300 disabled:text-neutral-300"
          >
            <ChevronRightIcon className="size-6" />
          </button>
        </div>
      </Splide>
    </div>
    <div className="flex">
      <InformationCircleIcon className="mr-2 size-5 shrink-0 text-teal-green" />
      <p className="mb-0 pt-[2px] text-xs text-neutral-500">
        <span className="font-semibold text-teal-green">
          Please note:&nbsp;&nbsp;
        </span>
        Images are for illustration purposes only. Actual product supplied may
        differ.
      </p>
    </div>
  </>
)

export default ImageCarousel
